
























































































































































import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { LPopup } from 'vue2-leaflet';
import {getLotsPrescriber, getProgramPrescriber} from '../../api/api';
import Program from '../../models/Program';
import ListModule from '@/store/ListModule';
import FiltersModule from '@/store/FiltersModule';
import { IMAGES_BASE_URL } from '@/helpers/constants';
import { getTaxSystemColor, getTaxSystemValue } from '@/data/taxSystems';

@Component({
    filters: {
        euro: function(value: number) {
            if (!value && value !== 0) return 'N.C.';
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 0,
            }).format(value);
        },
        formatYMD: (value: string) => {
            if (!value) return 'N.C.';
            try {
                let d = new Date(value);
                if (isNaN(d.getTime())) return 'N.C.';
                let mo = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(d);
                let da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(d);
                let year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(d);
                return `${da}/${mo}/${year}`;
            } catch {
                return 'N.C.';
            }
        },
    },
    components: {
        LPopup,
    },
})
export default class MapPopup extends Vue {
    @Prop() programID!: number;
    @Prop() opened!: boolean;
    @Prop({ default: false }) standalone!: boolean;
    loading: boolean = true;
    loaded: boolean = false;
    imageUrl: string | null = null;
    program: Program | null = null;
    error: boolean = false;

    mounted() {
        if (this.standalone) {
            this.onOpened();
        }
    }

    get selected() {
        return ListModule.programsBookmarked.find(
            program => program.id === this.programID
        );
    }

    get isPrivateMode() {
        return FiltersModule.privateMode;
    }

    toggle() {
        ListModule.selectProgram(this.programID);
    }

    @Watch('opened')
    async onOpened() {
        if (this.loaded || !this.opened) {
            return;
        }

        try {
            const { data } = await Promise.race([
                getProgramPrescriber(this.programID),
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Timeout')), 5000)
                )
            ]) as any;

            this.program = data;

            if (!this.program) {
                throw new Error('Program data is null');
            }

            // Wait for lots and image before revealing the popup
            await Promise.all([
                this.loadLotsAsync(),
                this.loadImageAsync(),
            ]);

            this.loading = false;
            this.loaded = true;
            this.error = false;

        } catch (error) {
            console.error('Failed to load program:', error);
            this.error = true;
            this.loading = false;
            this.loaded = true;
        }
    }

    async loadLotsAsync() {
        if (!this.program) return;

        try {
            const dataLots = await getLotsPrescriber(this.programID);
            const lots = dataLots.data || [];

            console.log(`[MapPopup] Program ${this.programID} — lots received:`, lots.length, lots);
            console.log(`[MapPopup] Lot statuses:`, lots.map((l: any) => l.status));

            const lotTypeCount: Record<string, { count: number; min: number; max: number }> = {};
            let libreCount = 0;

            for (const lot of lots) {
                const status = (lot.status || '').toLowerCase();
                // DB stores "available"; frontend may translate to "libre (xx-xx)"
                const isAvailable = status === 'available' || status.startsWith('libre');
                if (!isAvailable) continue;

                libreCount++;

                const type = lot.type2;
                const price = lot.price;

                if (!type || price == null || price <= 0 || price < 1000) continue;

                if (!lotTypeCount[type]) {
                    lotTypeCount[type] = { count: 1, min: price, max: price };
                } else {
                    lotTypeCount[type].count += 1;
                    lotTypeCount[type].min = Math.min(lotTypeCount[type].min, price);
                    lotTypeCount[type].max = Math.max(lotTypeCount[type].max, price);
                }
            }

            console.log(`[MapPopup] Program ${this.programID} — libre lots: ${libreCount}, lotTypeCount:`, lotTypeCount);

            // $set is required for Vue 2 to detect these as new reactive properties
            this.$set(this.program, 'nb_total_lots', libreCount);
            this.$set(this.program, 'lotTypeCount', lotTypeCount);

        } catch (lotsError) {
            console.warn('Failed to load lots:', lotsError);
            if (this.program) {
                this.$set(this.program, 'nb_total_lots', 0);
                this.$set(this.program, 'lotTypeCount', {});
            }
        }
    }

    async loadImageAsync() {
        if (!this.program || !this.program.primary_perspective) return;

        return new Promise<void>((resolve) => {
            try {
                const perspective = this.program!.primary_perspective;
                if (!perspective || !perspective.url || !perspective.name || !perspective.extension) {
                    this.imageUrl = null;
                    resolve();
                    return;
                }
                const imageUrl = `${IMAGES_BASE_URL}${perspective.url}${perspective.name}.${perspective.extension}`;

                const img = new Image();
                img.onload = () => { this.imageUrl = imageUrl; resolve(); };
                img.onerror = () => { this.imageUrl = null; resolve(); };
                img.src = imageUrl;
            } catch (imgError) {
                console.warn('Failed to load image:', imgError);
                this.imageUrl = null;
                resolve();
            }
        });
    }

    copyAddress(): void {
        if (this.program && this.program.address) {
            const { street, postal_code, city } = this.program.address;
            const fullAddress = `${street || ''} ${postal_code || ''} ${city || ''}`.trim();

            if (!fullAddress) {
                this.showErrorMessage('Adresse non disponible');
                return;
            }

            if ((navigator as any).clipboard && (window as any).isSecureContext) {
                (navigator as any).clipboard.writeText(fullAddress)
                    .then(() => this.showSuccessMessage())
                    .catch(() => this.oldMethodCopy(fullAddress));
            } else {
                this.oldMethodCopy(fullAddress);
            }
        }
    }

    oldMethodCopy(text: string): void {
        const input: HTMLInputElement = document.createElement('input');
        input.value = text;
        input.style.position = 'fixed';
        input.style.top = '-9999px';
        input.style.opacity = '0';

        document.body.appendChild(input);
        input.select();

        let copied: boolean = false;
        try {
            copied = document.execCommand('copy');
        } catch (e) {
            console.error('Erreur copie:', e);
        }

        document.body.removeChild(input);

        if (copied) {
            this.showSuccessMessage();
        } else {
            this.showErrorMessage();
        }
    }

    showSuccessMessage(): void {
        const $vs: any = (this as any).$vs;
        if ($vs && $vs.notify) {
            $vs.notify({
                color: 'success',
                position: 'top-right',
                title: 'Copié',
                text: 'Adresse copiée dans le presse-papier',
            });
        }
    }

    showErrorMessage(customText?: string): void {
        const $vs: any = (this as any).$vs;
        if ($vs && $vs.notify) {
            $vs.notify({
                color: 'danger',
                position: 'top-right',
                title: 'Erreur',
                text: customText || 'Impossible de copier l\'adresse automatiquement',
            });
        }
    }

    getTaxSystemColor(taxSystems: string[]) {
        if (taxSystems && taxSystems.length > 0) {
            const color = getTaxSystemColor(taxSystems[0]);
            if (color) {
                return color;
            }
        }
        return 'var(--vs-primary)';
    }

    getTaxSystemLabel(taxSystem: string) {
        return getTaxSystemValue(taxSystem) || taxSystem;
    }

    openProgramInTab() {
        const programName = this.program && this.program.name ? this.program.name : `Programme N° ${this.programID}`;
        this.$root.$emit('openProgramTab', this.programID, programName);
    }
}
